import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { AITool } from "@/data/categories";

// Module-level cache shared across all consumers, with a single in-flight
// promise so simultaneous mounts don't trigger N requests.
let cache: AITool[] | null = null;
let inflight: Promise<AITool[]> | null = null;
const listeners = new Set<(tools: AITool[]) => void>();

function rowToTool(row: {
  name: string;
  description: string | null;
  url: string | null;
  category: string;
  subcategory: string | null;
  pricing: string;
  trending: boolean;
  logo_url: string | null;
  tags?: string[] | null;
  model_type?: string | null;
  license?: string | null;
}): AITool {
  return {
    name: row.name,
    description: row.description ?? "",
    url: row.url ?? "",
    category: row.category,
    subcategory: row.subcategory ?? "",
    pricing: (row.pricing as AITool["pricing"]) ?? "Freemium",
    trending: row.trending,
    logoUrl: row.logo_url ?? undefined,
    tags: row.tags ?? [],
    modelType: row.model_type ?? "",
    license: row.license ?? "",
  };
}

async function fetchTools(): Promise<AITool[]> {
  // Supabase caps at 1000 rows per request — paginate to be safe as we grow.
  const PAGE = 1000;
  let from = 0;
  const all: AITool[] = [];
  while (true) {
    const { data, error } = await supabase
      .from("tools")
      .select(
        "name, description, url, category, subcategory, pricing, trending, logo_url, tags, model_type, license",
      )
      .eq("is_published", true)
      .order("name", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) {
      console.error("useTools: fetch failed", error);
      break;
    }
    if (!data || data.length === 0) break;
    all.push(...data.map(rowToTool));
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

function loadOnce(): Promise<AITool[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = fetchTools().then((t) => {
    cache = t;
    inflight = null;
    listeners.forEach((cb) => cb(t));
    return t;
  });
  return inflight;
}

export const useTools = () => {
  const [tools, setTools] = useState<AITool[]>(cache ?? []);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    listeners.add(setTools);
    if (!cache) {
      loadOnce().then(() => setLoading(false));
    } else {
      setLoading(false);
    }
    return () => {
      listeners.delete(setTools);
    };
  }, []);

  return { tools, loading };
};

export const refreshTools = async () => {
  cache = null;
  const t = await loadOnce();
  return t;
};
